// frontend/src/App.js
import React, { useContext, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { OnboardingContext } from './contexts/OnboardingContext';

import Dashboard from './components/Dashboard';
import BillingPanel from './components/dashboard/BillingPanel';
import BanksPanel from './components/dashboard/BanksPanel';
// PhonesPanel — DEPRECATED, merged into SettingsPanel. Routes redirect below.
import NotificationsPanel from './components/dashboard/NotificationsPanel';
// AccountPanel — DEPRECATED, merged into SettingsPanel. Routes redirect below.
import SettingsPanel from './components/settings/SettingsPanel';

// Planner components
import SubscriptionsPanel from './components/planner/SubscriptionsPanel';
import RecurringItemsPanel from './components/planner/RecurringItemsPanel';
import DebtPayoffPanel from './components/planner/DebtPayoffPanel';
// PlannerSettingsPanel — DEPRECATED, merged into SettingsPanel. Routes redirect below.
import ForecastPanel from './components/planner/ForecastPanel';
import HistoricalRegister from './components/planner/HistoricalRegister';
import MatchingPanel from './components/planner/MatchingPanel';
import BusinessEntitiesPanel from './components/planner/BusinessEntitiesPanel';
import SplitRulesPanel from './components/planner/SplitRulesPanel';
import ReportsPanel from './components/planner/ReportsPanel';
import AiAssistantPanel from './components/planner/AiAssistantPanel';
import { BusinessEntityProvider } from './contexts/BusinessEntityContext';

import LoginPage from './components/LoginPage';
import LoggedOutPage from './components/LoggedOutPage';
import ForgotPasswordRequest from './components/ForgotPasswordRequest';
import ForgotPassword        from './components/ForgotPassword';
import OAuthCallback from './components/OAuthCallback';

import SignUp from './pages/SignUp';
import Welcome from './pages/Welcome';
import AddPhone from './pages/AddPhone';
import OptInPolicy from './pages/OptInPolicy';
import ConfirmPhone from './pages/ConfirmPhone';
import ConnectBank from './pages/ConnectBank';
import ReviewDetected from './pages/ReviewDetected';
import ChoosePlan from './pages/ChoosePlan';
import Payment from './pages/Payment';
import PaymentSuccess from './pages/PaymentSuccess';
import CreateNotification from './pages/CreateNotification';
import NotificationsList from './pages/NotificationsList';
import EditNotification from './pages/EditNotification';
// import Dashboard from './components/Dashboard'; // duplicate import removed

import AdminConsole from './pages/AdminConsole';

import PrivateRoute from './components/PrivateRoute';
import OnboardGuard from './components/OnboardGuard';
import AuthOnboardLayout from './components/AuthOnboardLayout';

// Legal pages are code-split — ~700 LOC of static content doesn't need
// to ship in the main bundle for users who never visit /terms or /privacy.
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

function mapStepToPath(step){
  switch(step){
    case 'welcome': return '/welcome';
    case 'choose_plan': return '/choose-plan';
    case 'connect_bank': return '/connect-bank';
    case 'review_detected': return '/review-detected';
    default: return '/dashboard';
  }
}

function OnboardingIndexRedirect(){
  const { onboardingStep, onboardingLoading } = useContext(OnboardingContext);
  if (onboardingLoading) return null; // let layout render; avoid premature redirect
  const dest = mapStepToPath(onboardingStep);
  return <Navigate to={dest} replace />;
}

export default function App() {
  return (
    <Suspense fallback={null}>
      <Routes>

        {/** Public/OAuth **/}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/logged-out" element={<LoggedOutPage />} />
        <Route path="/forgot-password-request" element={<ForgotPasswordRequest />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/oauth-success" element={<OAuthCallback />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/** Sign-up kicks off the NEW-USER workflow **/}
        <Route path="/signup" element={<SignUp />} />

        {/** Everything below here requires a valid token **/}
        <Route path="/" element={<PrivateRoute><AuthOnboardLayout /></PrivateRoute>}>
          {/* default landing for authenticated users */}
          <Route index element={<OnboardingIndexRedirect />} />
          <Route path="dashboard" element={<BusinessEntityProvider><Dashboard /></BusinessEntityProvider>} />
          <Route path="dashboard/billing" element={<BusinessEntityProvider><BillingPanel /></BusinessEntityProvider>} />
          <Route path="dashboard/banks" element={<BusinessEntityProvider><BanksPanel /></BusinessEntityProvider>} />
          <Route path="dashboard/phones" element={<Navigate to="/settings" replace />} />
          <Route path="dashboard/notifications" element={<BusinessEntityProvider><NotificationsPanel /></BusinessEntityProvider>} />
          <Route path="dashboard/account" element={<Navigate to="/settings" replace />} />
          <Route path="settings" element={<BusinessEntityProvider><SettingsPanel /></BusinessEntityProvider>} />
          <Route path="add-phone" element={<AddPhone />} />
          <Route path="opt-in" element={<OptInPolicy />} />
          <Route path="confirm-phone" 
            element={
                  <ConfirmPhone />
            } 
          />

          <Route path="welcome" element={<Welcome />} />
          <Route path="choose-plan" element={<ChoosePlan />} />
          <Route path="payment" element={<Payment />} />
          <Route path="payment-success" element={<PaymentSuccess />} />
          <Route path="connect-bank"
            element={
                  <ConnectBank />
            }
          />
          <Route path="review-detected" element={<ReviewDetected />} />
          <Route path="create-notification" 
            element={
                  <CreateNotification />
            } 
          />
          <Route path="notifications" 
            element={
                  <NotificationsList />
            } 
          />
          <Route path="edit-notification/:id" 
            element={
                <EditNotification />
            } 
          />

          {/* Planner Routes — /planner redirects to forecast (old PlannerDashboard sunset) */}
          <Route path="planner" element={<Navigate to="/planner/forecast" replace />} />
          <Route path="planner/subscriptions" element={<BusinessEntityProvider><SubscriptionsPanel /></BusinessEntityProvider>} />
          <Route path="planner/recurring" element={<BusinessEntityProvider><RecurringItemsPanel /></BusinessEntityProvider>} />
          <Route path="planner/debts" element={<BusinessEntityProvider><DebtPayoffPanel /></BusinessEntityProvider>} />
          <Route path="planner/settings" element={<Navigate to="/settings" replace />} />
          <Route path="planner/forecast" element={<BusinessEntityProvider><ForecastPanel /></BusinessEntityProvider>} />
          <Route path="planner/history" element={<BusinessEntityProvider><HistoricalRegister /></BusinessEntityProvider>} />
          <Route path="planner/matching" element={<BusinessEntityProvider><MatchingPanel /></BusinessEntityProvider>} />
          <Route path="planner/business" element={<BusinessEntityProvider><BusinessEntitiesPanel /></BusinessEntityProvider>} />
          <Route path="planner/splits" element={<BusinessEntityProvider><SplitRulesPanel /></BusinessEntityProvider>} />
          <Route path="planner/transfers" element={<Navigate to="/planner/recurring" replace />} />
          <Route path="planner/reports" element={<BusinessEntityProvider><ReportsPanel /></BusinessEntityProvider>} />
          <Route path="planner/assistant" element={<BusinessEntityProvider><AiAssistantPanel /></BusinessEntityProvider>} />

          {/* Admin */}
          <Route path="admin" element={<AdminConsole />} />
        </Route>

        {/** Fallback **/}
        <Route path="*" element={<Navigate to="/login" replace />} />

      </Routes>
    </Suspense>
  );
}
